import { pb } from '../lib/pocketbase'
import type { FilterPreset } from './filterPresets'
import type { RecentItem } from './recentItems'

export type UserPrefs = {
  work_view_mode?: 'my-work' | 'team' | 'all'
  filter_presets?: FilterPreset[]
  /** Athletes the calendar is narrowed to (client ids). Empty = every athlete in scope. */
  calendar_athlete_focus?: string[]
  /** Whose dates the calendar draws: signed athletes, recruits, or both. */
  calendar_show_clients?: boolean
  calendar_show_prospects?: boolean
  /** Prospects this person is watching (prospect ids). Personal; see hooks/useWatchlist. */
  prospect_watchlist?: string[]
  recent_items?: RecentItem[]
  onboarding_complete?: boolean
  tour_completed?: boolean
  checklist_dismissed?: boolean
  team_welcome_seen?: boolean
  has_logged_in?: boolean
  theme?: 'light' | 'dark'
}

const LOCAL_KEY = 'dealpoint_preferences'
let syncTimer: ReturnType<typeof setTimeout> | null = null

function readLocal(): UserPrefs {
  try {
    const raw = localStorage.getItem(LOCAL_KEY)
    return raw ? (JSON.parse(raw) as UserPrefs) : {}
  } catch {
    return {}
  }
}

function writeLocal(prefs: UserPrefs): void {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(prefs))
  } catch {
    // Storage full or unavailable
  }
}

function scheduleSyncToServer(prefs: UserPrefs): void {
  if (syncTimer) clearTimeout(syncTimer)
  syncTimer = setTimeout(() => {
    const userId = pb.authStore.model?.id
    if (!userId || !pb.authStore.isValid) return
    pb.collection('users').update(userId, { preferences: prefs }).catch((err: Error) => {
      console.error('[prefs] preferences did not sync to the server; other devices will not see this change:', err)
    })
  }, 2000)
}

export function getPref<K extends keyof UserPrefs>(key: K): UserPrefs[K] {
  return readLocal()[key]
}

export function setPref<K extends keyof UserPrefs>(key: K, value: UserPrefs[K]): void {
  const prefs = { ...readLocal(), [key]: value }
  writeLocal(prefs)
  scheduleSyncToServer(prefs)
}

/**
 * Called once after authentication is confirmed.
 * Reads preferences from pb.authStore.model.preferences and seeds localStorage,
 * backfilling individual legacy keys so all existing code keeps working.
 */
export function loadPreferencesFromServer(): void {
  const model = pb.authStore.model
  if (!model) return

  const serverPrefs = (model as any).preferences
  if (!serverPrefs || typeof serverPrefs !== 'object') return

  const local = readLocal()
  const merged: UserPrefs = { ...local }

  for (const k of Object.keys(serverPrefs) as (keyof UserPrefs)[]) {
    const v = serverPrefs[k]
    if (v !== null && v !== undefined) {
      ;(merged as any)[k] = v
    }
  }

  writeLocal(merged)
  window.dispatchEvent(new CustomEvent('dealpoint:preferences-loaded', { detail: merged }))

  // Backfill individual localStorage keys so existing hook/utility code keeps working
  if (merged.work_view_mode) {
    localStorage.setItem('dealpoint_work_view_mode', merged.work_view_mode)
  }
  if (Array.isArray(merged.filter_presets)) {
    localStorage.setItem('dealpoint_filter_presets', JSON.stringify(merged.filter_presets))
  }
  if (Array.isArray(merged.recent_items)) {
    localStorage.setItem('dealpoint_recent_items', JSON.stringify(merged.recent_items))
  }
  if (merged.onboarding_complete) {
    localStorage.setItem('dealpoint_onboarding_complete', 'true')
  }
  if (merged.tour_completed) {
    localStorage.setItem('onboarding_tour_completed', 'true')
  }
  if (merged.checklist_dismissed) {
    localStorage.setItem('reppr_onboarding_checklist_dismissed', 'true')
  }
  if (merged.has_logged_in) {
    localStorage.setItem('dealpoint_has_logged_in', 'true')
  }
  if (merged.theme) {
    localStorage.setItem('theme', merged.theme)
    document.documentElement.setAttribute('data-theme', merged.theme)
  }
  if (merged.team_welcome_seen) {
    const userId = model.id
    if (userId) localStorage.setItem(`team_welcome_seen_${userId}`, 'true')
  }
}
